import { hhmm } from "./dateTimeHelpers.js";

export const hhmmToMinutes = (s) => {
  if (!s || typeof s !== "string" || !/^[0-2]?\d:[0-5]\d$/.test(s)) return null;
  const [h, m] = s.split(":");
  return parseInt(h, 10) * 60 + parseInt(m, 10);
};

export const minutesToHHMM = (m) => {
  const hh = String(Math.floor(m / 60)).padStart(2, "0");
  const mm = String(m % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

const addDaysLocal = (dateObj, days) => {
  const d = new Date(dateObj);
  d.setDate(d.getDate() + days);
  return d;
};

const getEasterSundayUTC = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
};

const toLocalISODate = (val) => {
  if (!val) return "";
  const d = val instanceof Date ? val : new Date(val);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const toISODateUTC = (val) => {
  if (!val) return "";
  const d = val instanceof Date ? val : new Date(val);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const toHMUTC = (val) => {
  if (!val) return "";
  const d = val instanceof Date ? val : new Date(val);
  const h = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${h}:${mm}`;
};

const getZAPublicHolidaysSet = (year) => {
  const holidays = [];

  holidays.push(new Date(year, 0, 1));
  holidays.push(new Date(year, 2, 21));

  const easterSundayUTC = getEasterSundayUTC(year);
  const easterSundayLocal = new Date(
    easterSundayUTC.getUTCFullYear(),
    easterSundayUTC.getUTCMonth(),
    easterSundayUTC.getUTCDate()
  );
  holidays.push(addDaysLocal(easterSundayLocal, -2));
  holidays.push(addDaysLocal(easterSundayLocal, 1));

  holidays.push(new Date(year, 3, 27));
  holidays.push(new Date(year, 4, 1));
  holidays.push(new Date(year, 5, 16));
  holidays.push(new Date(year, 7, 9));
  holidays.push(new Date(year, 8, 24));
  holidays.push(new Date(year, 11, 16));
  holidays.push(new Date(year, 11, 25));
  holidays.push(new Date(year, 11, 26));

  const set = new Set();
  for (const h of holidays) {
    const iso = toLocalISODate(h);
    if (iso) set.add(iso);

    const day = h.getDay();
    if (day === 0) {
      const observed = toLocalISODate(addDaysLocal(h, 1));
      if (observed) set.add(observed);
    }
  }
  return set;
};

export const isClosedDateLocal = (isoDate) => {
  if (!isoDate) return false;
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;

  const dow = d.getDay();
  if (dow === 0) return true;

  const holidaySet = getZAPublicHolidaysSet(d.getFullYear());
  return holidaySet.has(isoDate);
};

export const getOperatingHoursForDateLocal = (isoDate) => {
  if (!isoDate) return null;
  if (isClosedDateLocal(isoDate)) return null;

  const d = new Date(`${isoDate}T00:00:00`);
  const dow = d.getDay();

  if (dow >= 1 && dow <= 5) {
    return { startMin: 9 * 60, endMin: 13 * 60 };
  }

  if (dow === 6) {
    return { startMin: 9 * 60, endMin: 12 * 60 };
  }

  return null;
};

export const computeAvailableTimesForDate = ({
  isoDate,
  todayLocalISO,
  currentTime,
  existingAppointments,
  slotMinutes = 30,
}) => {
  if (!isoDate) return [];
  if (isoDate < todayLocalISO) return [];

  const hours = getOperatingHoursForDateLocal(isoDate);
  if (!hours) return [];

  const isToday = isoDate === todayLocalISO;
  const nowMin = currentTime.getHours() * 60 + currentTime.getMinutes();

  const operatingStart = hours.startMin;
  const operatingEnd = hours.endMin;
  const startMinRaw = isToday ? Math.max(nowMin, operatingStart) : operatingStart;
  const startMin = Math.max(operatingStart, Math.ceil(startMinRaw / slotMinutes) * slotMinutes);
  const lastStartMin = operatingEnd - slotMinutes;

  const bookedIntervals = (Array.isArray(existingAppointments) ? existingAppointments : [])
    .filter((a) => toISODateUTC(a.preferreddate) === isoDate)
    .map((a) => {
      const startHM = toHMUTC(a.preferredtime);
      const start = hhmmToMinutes(startHM);
      const end = Math.min(1440, (start ?? 0) + slotMinutes);
      return start == null ? null : [start, end];
    })
    .filter(Boolean);

  const overlaps = (s1, e1, s2, e2) => s1 < e2 && s2 < e1;

  const slots = [];
  if (startMin > lastStartMin) return [];

  for (let m = startMin; m <= lastStartMin; m += slotMinutes) {
    const s = m;
    const e = m + slotMinutes;
    const conflict = bookedIntervals.some(([bs, be]) => overlaps(s, e, bs, be));
    if (!conflict) slots.push(minutesToHHMM(m));
  }

  return slots;
};

export const defaultFollowUpTimeForSlots = (slots, fallbackDate) => {
  if (Array.isArray(slots) && slots.length > 0) return slots[0];
  if (fallbackDate instanceof Date && !Number.isNaN(fallbackDate.getTime())) return hhmm(fallbackDate);
  return "";
};
